import {
  FontAwesomeIcon,
  FontAwesomeIconProps,
} from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { MouseEventHandler, ReactNode, forwardRef } from "react";

interface IconAnchorProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  icon?: FontAwesomeIconProps["icon"];
  customIcon?: ReactNode;
  iconSize?: FontAwesomeIconProps["size"];
  color?: string;
  href: string;
  target?: string;
  size?: "small" | "medium" | "large";
  disabled?: boolean;
}

export const IconAnchor = forwardRef<HTMLAnchorElement, IconAnchorProps>(
  function IconAnchor(
    {
      icon,
      customIcon,
      iconSize,
      color = "text-primary-500 border-primary-500",
      size = "large",
      href,
      target,
      disabled = false,
      onClick,
      ...props
    },
    ref,
  ) {
    const sizeClasses = {
      small: "w-8 h-8",
      medium: "w-9 h-9",
      large: "w-11 h-11",
    };

    const handleClick: MouseEventHandler<HTMLAnchorElement> = (event) => {
      if (disabled) {
        event.preventDefault();
        return;
      }
      onClick?.(event);
    };

    return (
      // You may be itching to reorder the props for this anchor, but please don't! The order is important for the way the anchor is styled and how it behaves.

      <a
        ref={ref}
        href={disabled ? undefined : href}
        target={disabled ? undefined : target}
        onClick={handleClick}
        {...props}
        className={classNames(
          "rounded border transition-colors duration-200 inline-flex items-center justify-center",
          disabled
            ? "opacity-50 cursor-not-allowed text-gray-400 border-gray-300"
            : `hover:bg-primary-50 ${color}`,
          sizeClasses[size],
        )}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : undefined}
      >
        {!!customIcon && (
          <span className="flex justify-center items-center h-full">
            {customIcon}
          </span>
        )}
        {!!icon && <FontAwesomeIcon icon={icon} size={iconSize} />}
      </a>
    );
  },
);
